import type {
  GlobalCustomerRegistrationsRegistrationLegalDetailsDto,
  GlobalCustomersDocumentTypeDto,
} from '@ori/global-api-client-react';
import type { StaticCustomersMetadataIdentityCardDocumentPropertyDto } from '@ori/static-api-client-react';
import type { FC } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import type { IDocumentItemProps, IDocumentsInfoStoreData } from '../../interfaces';
import { TranslationKey } from '../../models';
import { useCustomerDocumentsInfos } from '../../models/api';
import { DatePickerField, SelectFormFieldByConfig, TextFormFieldByConfig } from '../FieldsByConfig';
import type { IFileUploadResult, IUploaderProps, IUploadResult } from '../Uploader';
import { Uploader } from '../Uploader';
import { getFilesInProgress, isDocumentPending, mapToDocumentItemProps, renderDocumentItem } from './DocumentActions';
import { DocumentHistory } from './DocumentHistory';
import { DocumentLoadingProgress } from './DocumentLoadingProgress';
import { StyledDivFileInput, StyledDivUploader, StyledDivWrapper, StyledListPending } from './DocumentStyledComponents';

interface IDocumentContentProps {
  documentTypeId: number;
  documentIds: number[];
  deleteConfirmationRequired?: boolean;
  editableDocument?: boolean;
  handleDocumentsChange?: (documentIds: number[]) => void;
  documentType?: GlobalCustomersDocumentTypeDto;
  documentInpuName: string;
  configDocument?: StaticCustomersMetadataIdentityCardDocumentPropertyDto;
  documentIsRequired: boolean;
}

export const DocumentContent: FC<IDocumentContentProps> = (props) => {
  const {
    documentIds,
    documentTypeId,
    deleteConfirmationRequired,
    documentType,
    editableDocument,
    documentInpuName,
    configDocument,
    documentIsRequired,
    handleDocumentsChange,
  } = props;

  const isTemporaryDocument = true;
  const hasDocuments = documentIds.length > 0;

  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadFinished, setIsUploadFinished] = useState(false);
  const [filesInProgress, setFilesInProgress] = useState<IDocumentItemProps[]>([]);
  const [fetchKey, setFetchKey] = useState('');

  const { watch } = useFormContext<GlobalCustomerRegistrationsRegistrationLegalDetailsDto>();
  const values = watch();

  const { data: documentsInfoData } = useCustomerDocumentsInfos(fetchKey);

  const isDeleteConfirmationRequired = deleteConfirmationRequired ?? false;

  const setDefaultState = () => {
    setIsHistoryExpanded(false);
    setIsUploading(false);
    setIsUploadFinished(false);
  };

  const removeFile = (fileKey: number) => {
    setDefaultState();
    setFilesInProgress((previousState) =>
      previousState.filter((documentData) => documentData.document.documentId !== fileKey),
    );
  };

  const fileUploadFinished = (fileUploadResult: IFileUploadResult) => {
    const { isSuccess, fileKey, message }: IFileUploadResult = fileUploadResult;

    if (!isSuccess && fileKey < 0 && Boolean(message)) {
      setDefaultState();
      setFilesInProgress((previousState) =>
        previousState.map((documentData) => {
          const copyDocumentData = { ...documentData };
          if (copyDocumentData.document.documentId === fileKey) {
            copyDocumentData.errorMessage = message;
          }

          return copyDocumentData;
        }),
      );
    }
  };

  const uploadProgress = (progress: number, fileKey: number) => {
    setDefaultState();
    setFilesInProgress((previousState) =>
      previousState.map((documentData) => {
        const copyDocumentData = { ...documentData };
        if (copyDocumentData.document.documentId === fileKey) {
          copyDocumentData.uploadProgress = progress;
        }

        return copyDocumentData;
      }),
    );
  };

  const uploadStart = (files: FileList) => {
    const negativeOne = -1;
    // eslint-disable-next-line unicorn/prefer-spread -- we want this writing
    const uploadFilesInProgress: IDocumentItemProps[] = Array.from(files).map((file, key) => ({
      document: {
        documentId: (key + 1) * negativeOne,
        fileName: file.name,
        fileSize: file.size,
      },
      isDeleteConfirmationRequired: false,
    }));

    setFilesInProgress(uploadFilesInProgress);
    setIsUploading(true);
  };

  const showUploadFinishedMessage = () => {
    setIsUploadFinished(true);
    setIsUploading(false);

    const messageHideTimeout = 2000;
    setTimeout(() => {
      setIsUploadFinished(false);
      setIsUploading(false);
    }, messageHideTimeout);
  };

  const triggerDocumentsChange = (propertyDocumentIds: number[]) => {
    if (handleDocumentsChange) {
      handleDocumentsChange(propertyDocumentIds);
    }
  };

  const uploadFinished = (result: IUploadResult) => {
    showUploadFinishedMessage();
    setFetchKey(Date.now().toString());
    const uploadedDocuments = result.uploadedFiles.filter((d) => d.documentId && d.documentId > 0 && d.isSuccess);

    if (uploadedDocuments.length === 0) {
      return;
    }
    const uploadedDocumentIds = uploadedDocuments.map((d) => d.documentId ?? 0);
    const correctDocumentIds = documentIds.length > 0 ? documentIds : [];
    const newDocumentIds = documentType?.allowMultipleDocuments
      ? [...correctDocumentIds, ...uploadedDocumentIds]
      : uploadedDocumentIds.slice(0, 1);

    triggerDocumentsChange(newDocumentIds);

    uploadedDocuments.forEach((d) => removeFile(d.fileKey));
  };

  const removeDocument = (documentId: number) => {
    const newDocuments = documentIds.filter((id) => id !== documentId);

    triggerDocumentsChange(newDocuments);
  };

  const getInfoByType = (typeId?: number, includeTemporary?: boolean) =>
    documentsInfoData
      ?.filter((info) => {
        const isTemporary = includeTemporary ?? false;

        return info.documentTypeId === typeId && (!info.isTemporary || isTemporary);
      })
      .sort((a, b) => {
        if (a.createdDate.getTime() && b.createdDate.getTime()) {
          return a.createdDate.getTime() - b.createdDate.getTime();
        }

        return 0;
      }) as IDocumentsInfoStoreData[];

  const getDocuments = () => {
    if (documentIds.length === 0) {
      return [];
    }
    const items = documentsInfoData ? getInfoByType(documentTypeId, isTemporaryDocument) : [];
    const documentItems: IDocumentItemProps[] = items
      .filter((document) => documentIds.includes(document.documentId) && isDocumentPending(document))
      .map((document) =>
        mapToDocumentItemProps(document, Boolean(editableDocument), true, isDeleteConfirmationRequired, removeDocument),
      );

    return documentItems;
  };

  const renderDocuments = () => {
    const getfilesInProgress: IDocumentItemProps[] = getFilesInProgress(filesInProgress, removeFile);
    const documents: IDocumentItemProps[] = getDocuments();

    const documentsToRender = [...documents, ...getfilesInProgress];

    if (documentsToRender.length > 0) {
      return <StyledListPending>{documentsToRender.map(renderDocumentItem)}</StyledListPending>;
    }

    return null;
  };

  const handleToggleClick = () => {
    setDefaultState();
    setIsHistoryExpanded(!isHistoryExpanded);
  };

  let fileInputProps: IUploaderProps = {
    isTemporary: isTemporaryDocument,
    handleFileUploaded: fileUploadFinished,
    handleUploadFinished: uploadFinished,
    handleUploadStart: uploadStart,
    handleFileUploadProgress: uploadProgress,
    inputName: documentInpuName,
    isRequired: documentIsRequired,
  };

  if (documentType) {
    fileInputProps = {
      ...fileInputProps,
      accept: documentType.supportedMimeTypes,
      documentTypeId: documentType.documentTypeId,
      maxFileSize: documentType.maxDataSize,
      isMultiple: documentType.allowMultipleDocuments,
    };
  }

  const uploaderDocument = () =>
    editableDocument ? (
      <StyledDivUploader>
        <StyledDivFileInput>
          <Uploader {...fileInputProps} />
        </StyledDivFileInput>
      </StyledDivUploader>
    ) : null;

  const documentCardTypeField = (
    <SelectFormFieldByConfig
      name="identityCard.cardType"
      label={
        <Localization
          id={TranslationKey.ThirdStep_DocumentType}
          defaultMessage={defaultTranslations.ThirdStep_DocumentType}
        />
      }
      title={
        <Localization
          id={TranslationKey.ThirdStep_ChooseDocumentType}
          defaultMessage={defaultTranslations.ThirdStep_ChooseDocumentType}
        />
      }
      placeholder={
        <Localization
          id={TranslationKey.ThirdStep_ChooseDocumentType}
          defaultMessage={defaultTranslations.ThirdStep_ChooseDocumentType}
        />
      }
      {...configDocument?.cardType}
    />
  );

  const documentCardNumberField = (
    <TextFormFieldByConfig
      name="identityCard.cardNumber"
      label={
        <Localization
          id={TranslationKey.ThirdStep_DocumentNumber}
          defaultMessage={defaultTranslations.ThirdStep_DocumentNumber}
        />
      }
      {...configDocument?.cardNumber}
    />
  );

  const disabledForIdentityCardTypes = configDocument?.expiration?.disabledForIdentityCardTypes;

  const disableExpirationField = disabledForIdentityCardTypes?.find(
    (cardType: string) => cardType === values.identityCard?.cardType,
  );

  const expirationDocumentField = !disableExpirationField && (
    <DatePickerField
      name="identityCard.expiration"
      label={
        <Localization
          id={TranslationKey.ThirdStep_ExpirationDate}
          defaultMessage={defaultTranslations.ThirdStep_ExpirationDate}
        />
      }
      invalidMsg={TranslationKey.validationDateRegex}
      {...configDocument?.expiration}
    />
  );

  const documents = renderDocuments();
  const uploader = uploaderDocument();

  return (
    <>
      {documentCardTypeField}
      {documentCardNumberField}
      {expirationDocumentField}
      <StyledDivWrapper ownerState={{ hasDocuments }}>
        {uploader}
        <DocumentLoadingProgress
          uploading={isUploading}
          uploadFinished={isUploadFinished}
        />
        {documents}
        {documentIds.length > 0 && (
          <DocumentHistory
            temporaryDocument={isTemporaryDocument}
            historyExpanded={isHistoryExpanded}
            documentIds={documentIds}
            documentTypeId={documentTypeId}
            handleToggleClick={handleToggleClick}
            documentPending={isDocumentPending}
            mapToDocumentItemProps={mapToDocumentItemProps}
            deleteConfirmationRequired={isDeleteConfirmationRequired}
            removeDocument={removeDocument}
            histroyDocumentsInfoData={documentsInfoData}
          />
        )}
      </StyledDivWrapper>
    </>
  );
};
